import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Layers, Code, Shield, Clock, TrendingUp, Globe } from "lucide-react";
import { Link } from "wouter";

export function MetricsCards() {
  // Use authentic compliance data directly instead of dashboard stats
  const { data: complianceData, isLoading } = useQuery({ 
    queryKey: ["/api/compliance/real-time"] 
  });
  const { data: stats } = useQuery({ 
    queryKey: ["/api/dashboard/stats"] 
  });

  // Extract authentic values from compliance API with fallbacks for new users
  const monitoredScripts = complianceData?.javascriptIntegrity?.totalScripts || stats?.totalScripts || 0;
  
  // Calculate integrity score based on real data or show appropriate message
  const integrityScore = (() => {
    // If we have real compliance data, use it (this comes from scan results)
    if (complianceData?.overallCompliance?.score !== undefined && complianceData?.overallCompliance?.score !== null) {
      return complianceData.overallCompliance.score;
    }
    
    // If no data is available, return null to show "No Data" message
    // Don't calculate from headers alone - wait for actual scan data
    return null;
  })();
  
  const externalDomains = complianceData?.networkCompliance?.thirdPartyDomains || stats?.thirdPartyApps || 0;
  const dailyScans = stats?.recentScans || 0;

  const cards = [
    {
      title: "JavaScript Assets",
      value: monitoredScripts,
      subtitle: `${stats?.activeApplications || 1} monitored endpoints`,
      icon: Code,
      iconColor: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/30",
      trend: "up",
      link: "/scripts", // Link to JavaScript inventory page
    },
    {
      title: "Security Score", 
      value: integrityScore !== null ? `${integrityScore}%` : "No Data",
      subtitle: integrityScore !== null ? "Real-time validation active" : "Run a scan to get started",
      icon: Shield,
      iconColor: integrityScore !== null ? "text-purple-600 dark:text-purple-400" : "text-gray-400 dark:text-gray-500", 
      bgColor: integrityScore !== null ? "bg-purple-100 dark:bg-purple-900/30" : "bg-gray-100 dark:bg-gray-800/30",
      trend: integrityScore !== null ? "shield" : "none",
      link: "/compliance", // Link to compliance monitoring page
    },
    {
      title: "Third-Party Services",
      value: externalDomains,
      subtitle: "Under active monitoring",
      icon: Layers,
      iconColor: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/30", 
      trend: "up",
      link: "/domains", // Link to domains page
    },
    {
      title: "Security Scans",
      value: dailyScans,
      subtitle: "Completed in last 24h",
      icon: Clock,
      iconColor: "text-indigo-600 dark:text-indigo-400",
      bgColor: "bg-indigo-100 dark:bg-indigo-900/30",
      trend: "time",
      link: "/scheduled-scans", // Link to scheduled scans page
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                  <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-16"></div>
                  <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-32"></div>
                </div>
                <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Handle case when no data is available for new users
  const hasData = monitoredScripts > 0 || externalDomains > 0 || dailyScans > 0;
  
  if (!hasData) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => (
          <Link key={index} href={card.link}>
            <Card className="cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200 border-dashed border-2 border-gray-300 dark:border-gray-600">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      {card.title}
                    </p>
                    <p className="text-3xl font-bold text-slate-400 dark:text-slate-500">
                      --
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center">
                      <span className="text-xs">No data available</span>
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${card.bgColor} rounded-lg flex items-center justify-center opacity-50`}>
                    <card.icon className={`w-6 h-6 ${card.iconColor}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map((card, index) => (
        <Link key={index} href={card.link}>
          <Card className="cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    {card.title}
                  </p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">
                    {card.value}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 flex items-center">
                    {card.trend === "up" && <TrendingUp className="w-3 h-3 mr-1" />}
                    {card.trend === "shield" && <Shield className="w-3 h-3 mr-1" />}
                    {card.trend === "time" && <Clock className="w-3 h-3 mr-1" />}
                    {card.subtitle}
                  </p>
                </div>
                <div className={`w-12 h-12 ${card.bgColor} rounded-lg flex items-center justify-center`}>
                  <card.icon className={`w-6 h-6 ${card.iconColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}
